import { useCallback, type SyntheticEvent } from 'react'

/**
 * THE FAILED-PHOTO CONTRACT — the SPA half.
 *
 * Photography on the marketing and public surfaces is always layered over a
 * warm gradient ground, and that ground is a finished surface on its own. So
 * when an image 404s, times out, or is blocked, the right answer is for the
 * image to get out of the way: the reader sees the gradient, never Chrome's
 * broken-image glyph, and never a photo credit for a photo that isn't there.
 *
 * Two halves, identical to the SSR kit's (`backend/src/shared/public-kit/
 * imgFallback.ts`):
 *
 *   1. MARK.  A failed `<img>` carries `data-img-failed`.
 *   2. HIDE.  CSS in `src/index.css` keyed on that attribute hides the image
 *             (`visibility: hidden`, so the reserved box and the layout are
 *             untouched) and, via `:has()`, the `[data-photo-credit]` chip
 *             inside the same `[data-photo-frame]`.
 *
 * The mark is set from THREE places, deliberately overlapping, because a photo
 * can fail in three different windows:
 *
 *   • Before any JS runs — the prerendered marketing pages are static HTML for
 *     hundreds of milliseconds before the bundle hydrates. Covered by the
 *     capture-phase listener inlined in `index.html` (`error` doesn't bubble,
 *     but it does reach capture-phase listeners on ancestors).
 *   • Between hydration and load — the React `onError` below.
 *   • Already failed at hydration — `onError` has nothing left to fire, so the
 *     ref callback reads the element's own completion state instead. This is
 *     also what re-applies the attribute if React's hydration pass dropped the
 *     one the inline listener set.
 *
 * Components render the attributes; nothing here needs component state, which
 * keeps the failure path out of the prerendered snapshot (the prerender strips
 * the marker — see `scripts/prerender-marketing.mjs`).
 */

export const IMAGE_FAILED_ATTR = 'data-img-failed'

/** True once the element has finished loading and produced no pixels. */
export function imageHasFailed(node: HTMLImageElement): boolean {
  // `currentSrc` is empty for an <img> that never had a resolvable source; that
  // is "nothing was asked for", not "the request failed".
  return node.complete && node.naturalWidth === 0 && node.currentSrc !== ''
}

export function markImageFailed(node: HTMLImageElement | null | undefined): void {
  node?.setAttribute(IMAGE_FAILED_ATTR, '')
}

export function clearImageFailed(node: HTMLImageElement | null | undefined): void {
  node?.removeAttribute(IMAGE_FAILED_ATTR)
}

/**
 * Handlers for a plain `<img>` that has no `LazyImage` around it. Spread the
 * result onto the element; put `data-photo-frame` on the box that owns the
 * gradient and `data-photo-credit` on the credit chip, and the CSS does the
 * rest.
 */
export function useImageFallback() {
  const ref = useCallback((node: HTMLImageElement | null) => {
    if (node && imageHasFailed(node)) markImageFailed(node)
  }, [])

  const onError = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    markImageFailed(event.currentTarget)
  }, [])

  // A swap to a working source (LazyImage's `fallback`, a re-render with a new
  // `src`) has to clear the mark, or the recovered photo stays invisible.
  const onLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    if (event.currentTarget.naturalWidth > 0) clearImageFailed(event.currentTarget)
  }, [])

  return { ref, onError, onLoad }
}
